/**
 * Self-ticking SLA pill.
 *
 * The whole point of this component is to be the *only* thing that
 * re-renders every second — pulling `useNow` down to the leaf so its
 * parent (a ticket row, peek panel, ticket detail) is stable across
 * ticks and can be memoised.
 *
 * Pre-fix: TriageInbox subscribed to `useNow(1000)` and passed `now`
 * down to every Row, causing 20+ rows × all child components to
 * reconcile every second. Page felt like a remote desktop. The
 * useNow comment block called this out as a v1 shortcut to be
 * replaced by Durable-Object subscriptions in v2; this component is
 * the lighter-weight v1.5 fix that gets ~95% of the perf benefit
 * without the DO infrastructure.
 *
 * Internal/BAU contracts have no SLA — render the same fixed-width
 * placeholder so list alignment doesn't shift on contract-type swap.
 */

import { memo } from 'react';

import type { Contract, Ticket } from '@beacon/core';

import { SLA } from '@/components/beacon/sla.js';
import { useNow } from '@/hooks/use-now.js';
import { slaSnapshotFor } from '@/lib/sla-display.js';

interface Props {
  ticket: Ticket;
  contract: Contract | undefined;
  /** Tick interval. 1000 ms is right for the active states (countdown
   *  ticking visibly); for static-text states (paused / met / breached
   *  with a fixed label) callers may pass a slower interval. */
  intervalMs?: number;
  /** When true, render nothing (leave the slot empty) instead of a
   *  placeholder dash. Useful when the parent already reserves space
   *  another way. */
  hidePlaceholder?: boolean;
}

export const SlaTicker = memo(function SlaTicker({
  ticket,
  contract,
  intervalMs = 1000,
  hidePlaceholder = false,
}: Props) {
  const now = useNow(intervalMs);
  const snap = slaSnapshotFor(ticket, contract, now);
  if (!snap) {
    return hidePlaceholder ? null : (
      <span className="text-[11.5px] text-ink-400 mono">—</span>
    );
  }
  return <SLA state={snap.state} label={snap.label} />;
});
